import type {
  HarmonyPackageBoard,
  HarmonyPackageBoardColumn,
  HarmonyPackageBoardWorkitem,
  HarmonyWorkitem,
  Workitem,
} from '@rhythmic/db/types'
import type { KanbanCard } from '@rhythmic/types'
import type { Collection, UseLiveQueryReturn } from '@tanstack/vue-db'
import type { Hookable } from 'hookable'
import type { Ref } from 'vue'

import type { HarmonyItem } from '../../../queries'
import type { KanbanBoardContextEvents } from '../useKanbanBoardContext.events.schema'

type KanbanBoardContextHookMap = {
  [K in keyof KanbanBoardContextEvents]: (event: KanbanBoardContextEvents[K]) => void | Promise<void>
}

/**
 * Handle for removing a previously registered kanban board context hook.
 */
type KanbanBoardContextUnsubscribe = () => void

interface KanbanBoardContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface UseKanbanBoardContextOnContextChangedParams {
  hooks: Hookable<KanbanBoardContextHookMap>
  logger: KanbanBoardContextLoggerLike
  boardQuery: Ref<UseLiveQueryReturn<HarmonyPackageBoard> | undefined>
  columnsQuery: Ref<UseLiveQueryReturn<HarmonyPackageBoardColumn> | undefined>
  boardItemsQuery: Ref<UseLiveQueryReturn<KanbanCard> | undefined>
  harmonyItemsQuery: Ref<UseLiveQueryReturn<HarmonyItem> | undefined>
  boardsCollection: Ref<Collection<HarmonyPackageBoard> | undefined>
  columnsCollection: Ref<Collection<HarmonyPackageBoardColumn> | undefined>
  boardItemsCollection: Ref<Collection<HarmonyPackageBoardWorkitem> | undefined>
  packageItemsCollection: Ref<Collection<HarmonyWorkitem> | undefined>
  workitemsCollection: Ref<Collection<Workitem> | undefined>
  disposeResources: () => void
  clearBoardScopedState: () => void
  factories: {
    createBoardsCollection: (harmonyPlanId: string) => Collection<HarmonyPackageBoard>
    createColumnsCollection: (boardId: string) => Collection<HarmonyPackageBoardColumn>
    createBoardItemsCollection: (boardId: string) => Collection<HarmonyPackageBoardWorkitem>
    createPackageItemsCollection: (harmonyPlanId: string) => Collection<HarmonyWorkitem>
    createWorkitemsCollection: (teamId: string) => Collection<Workitem>
    createBoardQuery: (boardId: string, harmonyPlanId: string) => UseLiveQueryReturn<HarmonyPackageBoard> | undefined
    createColumnsQuery: (boardId: string) => UseLiveQueryReturn<HarmonyPackageBoardColumn> | undefined
    createBoardItemsQuery: (
      boardId: string,
      workspaceId: string,
      harmonyPlanId: string,
      teamId: string,
    ) => UseLiveQueryReturn<KanbanCard> | undefined
    createHarmonyItemsQuery: (harmonyPlanId: string, teamId: string) => UseLiveQueryReturn<HarmonyItem> | undefined
  }
}

/**
 * Internal handler: configures backing collections + live queries when the resolved kanban
 * context identifiers change.
 *
 * It clears and disposes existing resources when the context becomes incomplete, and
 * dedupes re-initialization using a stable `${workspaceId}:${teamId}:${planId}:${boardId}` key.
 */
export default function useKanbanBoardContextOnContextChanged({
  hooks,
  logger,
  boardQuery,
  columnsQuery,
  boardItemsQuery,
  harmonyItemsQuery,
  boardsCollection,
  columnsCollection,
  boardItemsCollection,
  packageItemsCollection,
  workitemsCollection,
  disposeResources,
  clearBoardScopedState,
  factories,
}: UseKanbanBoardContextOnContextChangedParams) {
  let lastContextKey: string | undefined

  const unsubscribe: KanbanBoardContextUnsubscribe = hooks.hook(
    'ContextChanged',
    ({
      harmonyPlanId,
      boardId,
      teamId,
      workspaceId,
      previousHarmonyPlanId,
      previousBoardId,
      previousTeamId,
      previousWorkspaceId,
    }) => {
      const isMissingContext = !harmonyPlanId || !boardId || !teamId || !workspaceId

      if (isMissingContext) {
        lastContextKey = undefined
        disposeResources()
        clearBoardScopedState()
        return
      }

      const nextContextKey = `${workspaceId}:${teamId}:${harmonyPlanId}:${boardId}`
      if (nextContextKey === lastContextKey) return

      lastContextKey = nextContextKey

      disposeResources()
      clearBoardScopedState()

      boardsCollection.value = factories.createBoardsCollection(harmonyPlanId)
      columnsCollection.value = factories.createColumnsCollection(boardId)
      boardItemsCollection.value = factories.createBoardItemsCollection(boardId)
      packageItemsCollection.value = factories.createPackageItemsCollection(harmonyPlanId)
      workitemsCollection.value = factories.createWorkitemsCollection(teamId)

      boardQuery.value = factories.createBoardQuery(boardId, harmonyPlanId)
      columnsQuery.value = factories.createColumnsQuery(boardId)
      boardItemsQuery.value = factories.createBoardItemsQuery(boardId, workspaceId, harmonyPlanId, teamId)
      harmonyItemsQuery.value = factories.createHarmonyItemsQuery(harmonyPlanId, teamId)
    },
  )

  return unsubscribe
}
